/*******************************************************************************
 * Copyright (c) 2023 GetChkd
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 2.0.0
 *
 *******************************************************************************/
import React, { useState } from 'react';
import {
    Grid,
    Typography,
    FormControl,
    Button,
    CircularProgress,
    InputAdornment,
    IconButton,
    Box
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { patchPassword } from '../../../../../../services/user/UserController';

const UserUpdatePassword = ({ toggleEditPwd, toggleEdit }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <Box m={3}>
            <Formik
                initialValues={{
                    password: '',
                    confirmPassword: ''
                }}
                validationSchema={Yup.object({
                    password: Yup.string().min(8).max(30).required('Password is required'),
                    confirmPassword: Yup.string()
                        .min(8)
                        .max(30)
                        .oneOf([Yup.ref('password'), null], 'Passwords must match')
                        .required('Confirm password is required'),
                })}
                onSubmit={async ({ password }, { setSubmitting }) => {
                    try {
                        await patchPassword({ password }).unwrap();
                        toast.info('Password changed successfully');
                        toggleEditPwd(false);
                        toggleEdit(false);
                    } catch (error) {
                        toast.error('Error changing password');
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, values, errors, touched, handleBlur, handleChange }) => (
                    <Form>
                        <Grid container spacing={4} sx={{ padding: 5 }}>
                            <Grid item xs={12} sm={4} md={4} />
                            <Grid item xs={12} sm={4} md={4}>
                                <Typography variant="h3" color="primary" align="center">
                                    Change My Password
                                </Typography>

                                <Grid container spacing={2}>
                                    {/* Password Field */}
                                    <Grid item xs={12}>
                                        <FormControl fullWidth error={touched.password && !!errors.password}>
                                            <Field
                                                component={TextField}
                                                name="password"
                                                label="New Password"
                                                type={showPassword ? 'text' : 'password'}
                                                variant="outlined"
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.password}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>

                                    {/* Confirm Password Field */}
                                    <Grid item xs={12}>
                                        <FormControl fullWidth error={touched.confirmPassword && !!errors.confirmPassword}>
                                            <Field
                                                component={TextField}
                                                name="confirmPassword"
                                                label="Confirm New Password"
                                                type={showPassword ? 'text' : 'password'}
                                                variant="outlined"
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.confirmPassword}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Buttons */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
                                            {isSubmitting && <CircularProgress size={24} color="warning" />} Save
                                        </Button>
                                        <Button variant="outlined" sx={{ ml: 2 }} onClick={() => toggleEditPwd(false)}>
                                            Cancel
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UserUpdatePassword;

